import React, { useEffect, useState } from 'react';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { MdOutlineWork as WorkIcon } from 'react-icons/md';
import { IoSchool as SchoolIcon } from 'react-icons/io5';
import { FaStar as StarIcon } from 'react-icons/fa';
import './WorkExperience.css';
import { TimelineItem } from '../types';
import { getTimeline } from '../data/localDataService';

/**
 * Card color variants. react-vertical-timeline-component applies contentStyle /
 * contentArrowStyle inline, so these literals must stay here; all typography and
 * layout styling lives in WorkExperience.css. Arrow borders match each card
 * background so the pointer reads as part of the card.
 */
const CARD_FEATURED = {
  contentStyle: { background: '#2a1215', color: '#ffffff' },
  contentArrowStyle: { borderRight: '7px solid #2a1215' },
};

const CARD_DEFAULT = {
  contentStyle: { background: '#1c1c1f', color: '#f5f5f7' },
  contentArrowStyle: { borderRight: '7px solid #1c1c1f' },
};

const WorkExperience: React.FC = () => {
  const [timeLineData, setTimeLineData] = useState<TimelineItem[] | null>(null);

  useEffect(() => {
    async function fetchTimelineItem() {
      const data = await getTimeline();
      setTimeLineData(data);
    }
    fetchTimelineItem();
  }, []);

  if (!timeLineData) return (
    <div role="status" aria-live="polite" aria-busy="true">
      Loading work experience...
    </div>
  );


  return (
    <article itemScope itemType="https://schema.org/Person">
      <section className="timeline-container" aria-labelledby="timeline-heading">
        <h1 id="timeline-heading" className="timeline-title">
          Work Experience &amp; Education Timeline
        </h1>
        <p className="timeline-subtitle">
          Professional journey and academic background of Shivom Sharma
        </p>
      </section>

      <section aria-label="Career timeline">
        <VerticalTimeline className="experience-timeline">
          {timeLineData.map((item, index) => {
            const isFeaturedWork = item.timelineType === 'work' && index === 0;
            return (
              <VerticalTimelineElement
                key={index}
                className={`vertical-timeline-element--${item.timelineType}`}
                {...(isFeaturedWork ? CARD_FEATURED : CARD_DEFAULT)}
                date={item.dateRange}
                iconStyle={{
                  background: item.timelineType === 'work' ? '#e50914' : '#3a3a3f',
                  color: '#ffffff',
                }}
                icon={item.timelineType === 'work' ? <WorkIcon aria-hidden="true" /> : <SchoolIcon aria-hidden="true" />}
              >
                {item.timelineType === 'work' ? (
                  <div itemScope itemType="https://schema.org/OrganizationRole">
                    <h2 className="vertical-timeline-element-title" itemProp="roleName">
                      {item.title}
                    </h2>
                    <h3 className="vertical-timeline-element-subtitle">
                      {item.companyLink ? (
                        <a
                          href={item.companyLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="company-link"
                          itemProp="memberOf"
                          aria-label={`Visit ${item.name} website (opens in new tab)`}
                        >
                          {item.name} <span aria-hidden="true">🔗</span>
                        </a>
                      ) : (
                        <span itemProp="memberOf">{item.name}</span>
                      )}
                    </h3>
                    <p className="vertical-timeline-element-tech">
                      <strong>Technologies:</strong> {item.techStack}
                    </p>
                    <ul className="timeline-points" itemProp="description">
                      {item.summaryPoints.map((point, i) => (
                        <li key={i}>{point}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div itemScope itemType="https://schema.org/EducationalOccupationalCredential">
                    <h2 className="vertical-timeline-element-title" itemProp="educationalLevel">
                      {item.name}
                    </h2>
                    <h3 className="vertical-timeline-element-subtitle" itemProp="credentialCategory">
                      {item.title}
                    </h3>
                    <ul className="timeline-points" itemProp="description">
                      {item.summaryPoints.map((point, i) => (
                        <li key={i}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </VerticalTimelineElement>
            );
          })}
          <VerticalTimelineElement
            iconStyle={{ background: '#10cc82', color: '#ffffff' }}
            icon={<StarIcon aria-label="Beginning of career" />}
          />
        </VerticalTimeline>
      </section>
    </article>
  );
};

export default WorkExperience;
